/**
 * Morceaux les plus sollicités — un tableau, pas un graphique.
 *
 * Quatre mesures par morceau et des noms longs : un graphique de barres ne
 * porterait qu'une des quatre et couperait les libellés. Le tableau les montre
 * toutes, se trie à l'œil, et se copie dans un tableur.
 *
 * La barre dans la cellule rend la magnitude comparable d'un coup d'œil, sans
 * enlever le nombre : la longueur donne la forme, le chiffre donne la valeur.
 *
 * `trackName` est le nom du fichier `.mid` — c'est la clé du catalogue de prod
 * (`songs.full_name`). Afficher auteur et titre demanderait de synchroniser
 * aussi la table `songs` de `appdb` ; c'est l'extension la plus évidente.
 */

import { formatTrackName } from '@shared/types/stats';

import { formatInteger } from '../lib/format';
import { COLORS } from '../lib/theme';
import type { TopTrackItem } from '../types';
import { Card } from './Card';

export function TopTracksTable({ data }: { data: readonly TopTrackItem[] }) {
  if (data.length === 0) {
    return (
      <Card title="Morceaux les plus joués" className="chart-grid__wide">
        <p className="empty" style={{ height: 120 }}>
          Aucune action rattachée à un morceau sur cette période.
        </p>
      </Card>
    );
  }

  // Les donnees arrivent triees par nombre d'actions decroissant : le premier
  // element donne l'echelle des barres.
  const maxActions = Math.max(...data.map((track) => track.actions), 1);

  return (
    <Card
      title="Morceaux les plus joués"
      subtitle="Survoler une ligne pour voir le nom de fichier d'origine"
      className="chart-grid__wide"
    >
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th style={{ width: '40px' }} className="num">
                #
              </th>
              <th>Morceau</th>
              <th className="num">Actions</th>
              <th style={{ width: '22%' }}>
                <span className="sr-only">Magnitude relative</span>
              </th>
              <th className="num">Lectures</th>
              <th className="num">Apprentissages</th>
              <th className="num">Utilisateurs</th>
            </tr>
          </thead>
          <tbody>
            {data.map((track, index) => (
              <tr key={track.trackName}>
                <td className="num" style={{ color: COLORS.textMuted }}>
                  {index + 1}
                </td>
                {/* Nom mis en forme : un nom de fichier brut se lit mal et se
                    compare encore moins bien. Le nom d'origine reste en base. */}
                <td className="wrap" title={track.trackName}>
                  {formatTrackName(track.trackName)}
                </td>
                <td className="num">{formatInteger(track.actions)}</td>
                <td>
                  <div
                    className="cell-bar"
                    style={{ width: `${(track.actions / maxActions) * 100}%` }}
                    aria-hidden="true"
                  />
                </td>
                <td className="num">{formatInteger(track.plays)}</td>
                <td className="num">{formatInteger(track.learns)}</td>
                <td className="num">{formatInteger(track.uniqueUsers)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
